import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { complianceApi, restaurantApi } from '../services/api';
import {
  Save,
  Loader2,
  CheckCircle,
  Mail,
  FileText,
  MapPin,
  Phone,
  User,
  Shirt,
  Clock,
  Car,
  BookOpen,
  Link2,
  Eye,
  ArrowLeft,
} from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import HelpButton from '../components/HelpButton';

interface OnboardingInfo {
  id?: string;
  welcomeMessage?: string;
  dressCode?: string;
  uniformRequirements?: string;
  groomingStandards?: string;
  serviceStandards?: string;
  firstDayInstructions?: string;
  parkingInfo?: string;
  menuUrl?: string;
  trainingMaterialsUrl?: string;
  employeeHandbookUrl?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
}

export default function OnboardingSettingsPage() {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<OnboardingInfo>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Fetch current onboarding info
  const { data: onboardingData, isLoading } = useQuery({
    queryKey: ['onboarding-info'],
    queryFn: async () => {
      const response = await complianceApi.getOnboardingInfo();
      return response.data?.data || null;
    },
  });

  // Fetch restaurant info for preview
  const { data: restaurantData } = useQuery({
    queryKey: ['restaurant-current'],
    queryFn: async () => {
      const response = await restaurantApi.getCurrent();
      return response.data?.data || response.data;
    },
  });

  // Build restaurant address string
  const getRestaurantAddress = () => {
    if (!restaurantData) return '';
    const parts = [
      restaurantData.addressStreet1,
      restaurantData.addressStreet2,
      restaurantData.addressCity,
      restaurantData.addressState,
      restaurantData.addressPostalCode,
    ].filter(Boolean);
    return parts.join(', ');
  };

  // Load data into form - use restaurant data as defaults if no onboarding info exists
  useEffect(() => {
    if (onboardingData) {
      // Use existing onboarding data, but fill in missing fields from restaurant
      setFormData({
        ...onboardingData,
        contactName: onboardingData.contactName || restaurantData?.ownerName || '',
        contactPhone: onboardingData.contactPhone || restaurantData?.phone || '',
        contactEmail: onboardingData.contactEmail || restaurantData?.email || '',
        firstDayInstructions: onboardingData.firstDayInstructions || (restaurantData ? `Please arrive 15 minutes before your scheduled shift at:\n\n${getRestaurantAddress()}\n\nBring a valid ID and wear comfortable, non-slip shoes.` : ''),
      });
    } else if (restaurantData) {
      // No onboarding data yet - pre-populate with restaurant defaults
      const address = getRestaurantAddress();
      setFormData({
        contactName: restaurantData.ownerName || '',
        contactPhone: restaurantData.phone || '',
        contactEmail: restaurantData.email || '',
        welcomeMessage: `Welcome to ${restaurantData.name}! We're thrilled to have you join our team. We can't wait to work with you and help you succeed in your new role.`,
        firstDayInstructions: `Please arrive 15 minutes before your scheduled shift at:

${address || '[Restaurant Address]'}

What to bring:
• Government-issued photo ID (driver's license or passport)
• Social Security card or birth certificate
• Comfortable, closed-toe, non-slip shoes
• A pen and small notepad

When you arrive, ask for the manager on duty. They will give you a tour, introduce you to the team, and get you set up with your login credentials.

Plan for about 30-60 minutes of orientation before your first shift begins.`,
        dressCode: `Please wear clean, professional attire:

• All black clothing preferred (or dark colors)
• No visible logos, graphics, or ripped clothing
• Closed-toe, non-slip shoes required for safety
• Hair must be neat and tied back if long
• Minimal jewelry (small earrings, wedding bands only)
• Clean, trimmed nails
• Light fragrance or none`,
        uniformRequirements: 'A company shirt will be provided on your first day.',
        groomingStandards: 'Professional appearance required. Hair tied back, clean nails, minimal jewelry.',
        serviceStandards: `At ${restaurantData.name}, we pride ourselves on exceptional hospitality. Our service philosophy:

• Greet every guest with a warm smile within 30 seconds
• Anticipate needs before being asked
• Own every interaction - never say "that's not my section"
• Handle complaints gracefully and involve a manager when needed
• Thank guests sincerely as they leave

We treat every guest like they're our most important customer, because they are.`,
        parkingInfo: address ? `Employee parking:
• Park in the designated employee area (ask your manager)
• Do not park in guest spots or handicap spaces
• Street parking may be available after 6pm on ${restaurantData.addressStreet1 || 'nearby streets'}

Public transit options may also be available - check with your manager for recommendations.` : '',
      });
    }
  }, [onboardingData, restaurantData]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data: OnboardingInfo) => complianceApi.updateOnboardingInfo(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-info'] });
      setHasChanges(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    },
  });

  const handleChange = (field: keyof OnboardingInfo, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    saveMutation.mutate(formData);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <RouterLink to="/staff" className="p-2 hover:bg-slate-100 rounded-lg">
            <ArrowLeft className="w-5 h-5 text-slate-500" />
          </RouterLink>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-slate-900">Onboarding Email Settings</h1>
              <HelpButton featureId="onboarding-settings" />
            </div>
            <p className="text-slate-500">Configure what new employees see in their welcome email</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="btn btn-secondary flex items-center gap-2"
          >
            <Eye className="w-4 h-4" />
            {showPreview ? 'Hide Preview' : 'Preview Email'}
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges || saveMutation.isPending}
            className="btn btn-primary flex items-center gap-2 disabled:opacity-50"
          >
            {saveMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : saveSuccess ? (
              <CheckCircle className="w-4 h-4" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {saveSuccess ? 'Saved!' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Preview Panel */}
      {showPreview && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Email Preview
          </h2>
          <div className="border border-slate-200 rounded-lg p-6 bg-slate-50 max-h-[600px] overflow-y-auto">
            <div className="bg-white rounded-lg shadow-sm p-6 max-w-xl mx-auto">
              <h1 className="text-2xl font-bold text-slate-900 mb-4">
                Welcome to {restaurantData?.name || 'Your Restaurant'}!
              </h1>

              {formData.welcomeMessage ? (
                <p className="text-slate-700 mb-4 whitespace-pre-line">{formData.welcomeMessage}</p>
              ) : (
                <p className="text-slate-700 mb-4">
                  Hi [Employee Name],<br />
                  Congratulations! You've been invited to join the team at <strong>{restaurantData?.name || 'our restaurant'}</strong> as a <strong>[Role]</strong>.
                </p>
              )}

              {/* POS Info Box */}
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg mb-4">
                <h3 className="font-semibold text-blue-900 mb-2">Your POS Login Information</h3>
                <p className="text-blue-800"><strong>Restaurant Code:</strong> <code className="bg-white px-2 py-0.5 rounded">{restaurantData?.slug || 'restaurant-code'}</code></p>
                <p className="text-blue-800"><strong>Your PIN:</strong> <code className="bg-white px-2 py-0.5 rounded text-lg">1234</code></p>
                <p className="text-sm text-blue-700 mt-2">Use this PIN to clock in/out and access the POS system.</p>
              </div>

              {/* Restaurant Location */}
              {restaurantData && (
                <div className="bg-slate-100 p-4 rounded-lg mb-4">
                  <h3 className="font-semibold text-slate-900 mb-2">Restaurant Location</h3>
                  <p className="text-slate-700">
                    {[restaurantData.addressStreet1, restaurantData.addressCity, restaurantData.addressState, restaurantData.addressPostalCode].filter(Boolean).join(', ')}
                  </p>
                  {restaurantData.phone && <p className="text-slate-700"><strong>Phone:</strong> {restaurantData.phone}</p>}
                </div>
              )}

              {/* Dress Code */}
              {(formData.dressCode || formData.uniformRequirements) && (
                <div className="bg-amber-50 p-4 rounded-lg mb-4">
                  <h3 className="font-semibold text-amber-900 mb-2">Dress Code & Uniform</h3>
                  {formData.dressCode && <p className="text-amber-800 whitespace-pre-line">{formData.dressCode}</p>}
                  {formData.uniformRequirements && <p className="text-amber-800 mt-2"><strong>Uniform:</strong> {formData.uniformRequirements}</p>}
                </div>
              )}

              {/* Service Standards */}
              {formData.serviceStandards && (
                <div className="bg-slate-100 p-4 rounded-lg mb-4">
                  <h3 className="font-semibold text-slate-900 mb-2">Service Standards</h3>
                  <p className="text-slate-700 whitespace-pre-line">{formData.serviceStandards}</p>
                </div>
              )}

              {/* First Day Instructions */}
              {formData.firstDayInstructions && (
                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg mb-4">
                  <h3 className="font-semibold text-green-900 mb-2">First Day Instructions</h3>
                  <p className="text-green-800 whitespace-pre-line">{formData.firstDayInstructions}</p>
                </div>
              )}

              {/* Parking */}
              {formData.parkingInfo && (
                <div className="bg-slate-100 p-4 rounded-lg mb-4">
                  <h3 className="font-semibold text-slate-900 mb-2">Parking</h3>
                  <p className="text-slate-700 whitespace-pre-line">{formData.parkingInfo}</p>
                </div>
              )}

              {/* Links */}
              {(formData.menuUrl || formData.trainingMaterialsUrl) && (
                <div className="bg-slate-100 p-4 rounded-lg mb-4">
                  <h3 className="font-semibold text-slate-900 mb-2">Training Resources</h3>
                  {formData.menuUrl && <p><a href={formData.menuUrl} className="text-primary-600 hover:underline">View Our Menu</a></p>}
                  {formData.trainingMaterialsUrl && <p><a href={formData.trainingMaterialsUrl} className="text-primary-600 hover:underline">Training Materials</a></p>}
                </div>
              )}

              {/* Contact Info */}
              {(formData.contactName || formData.contactPhone || formData.contactEmail) && (
                <div className="bg-purple-50 p-4 rounded-lg mb-4">
                  <h3 className="font-semibold text-purple-900 mb-2">Questions? Contact Us</h3>
                  {formData.contactName && <p className="text-purple-800 font-medium">{formData.contactName}</p>}
                  {formData.contactPhone && <p className="text-purple-800">Phone: {formData.contactPhone}</p>}
                  {formData.contactEmail && <p className="text-purple-800">Email: <a href={`mailto:${formData.contactEmail}`} className="text-primary-600">{formData.contactEmail}</a></p>}
                </div>
              )}

              <p className="text-sm text-slate-500 mt-4">
                This link expires in 7 days.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Form Sections */}
      <div className="space-y-6">
        {/* Welcome Message */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-slate-900">Welcome Message</h2>
          </div>
          <p className="text-sm text-slate-500 mb-3">
            Custom welcome message that appears at the top of the email. Leave blank for default.
          </p>
          <textarea
            value={formData.welcomeMessage || ''}
            onChange={(e) => handleChange('welcomeMessage', e.target.value)}
            placeholder="Hi [Name], We're so excited to have you join our team! You're going to love working here..."
            className="input w-full h-32 resize-none"
          />
        </div>

        {/* Dress Code & Uniform */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Shirt className="w-5 h-5 text-amber-600" />
            <h2 className="text-lg font-semibold text-slate-900">Dress Code & Uniform</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Dress Code</label>
              <textarea
                value={formData.dressCode || ''}
                onChange={(e) => handleChange('dressCode', e.target.value)}
                placeholder="All black attire. No visible logos or graphics. Closed-toe non-slip shoes required."
                className="input w-full h-24 resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Uniform Requirements</label>
              <input
                type="text"
                value={formData.uniformRequirements || ''}
                onChange={(e) => handleChange('uniformRequirements', e.target.value)}
                placeholder="Company t-shirt provided on first day, black pants/jeans"
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Grooming Standards</label>
              <input
                type="text"
                value={formData.groomingStandards || ''}
                onChange={(e) => handleChange('groomingStandards', e.target.value)}
                placeholder="Hair must be tied back, minimal jewelry, clean nails"
                className="input w-full"
              />
            </div>
          </div>
        </div>

        {/* Service Standards */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-emerald-600" />
            <h2 className="text-lg font-semibold text-slate-900">Service Standards</h2>
          </div>
          <textarea
            value={formData.serviceStandards || ''}
            onChange={(e) => handleChange('serviceStandards', e.target.value)}
            placeholder="- Greet guests within 30 seconds of being seated
- Offer water and bread immediately
- Check back within 2 minutes of food delivery
- Never say 'that's not my table'"
            className="input w-full h-32 resize-none"
          />
        </div>

        {/* First Day Instructions */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-green-600" />
            <h2 className="text-lg font-semibold text-slate-900">First Day Instructions</h2>
          </div>
          <textarea
            value={formData.firstDayInstructions || ''}
            onChange={(e) => handleChange('firstDayInstructions', e.target.value)}
            placeholder="Please arrive 15 minutes early for orientation.
Enter through the back door on Oak Street.
Ask for the manager on duty.
Bring your ID and any required documents."
            className="input w-full h-32 resize-none"
          />
        </div>

        {/* Parking */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Car className="w-5 h-5 text-slate-600" />
            <h2 className="text-lg font-semibold text-slate-900">Parking Information</h2>
          </div>
          <textarea
            value={formData.parkingInfo || ''}
            onChange={(e) => handleChange('parkingInfo', e.target.value)}
            placeholder="Employee parking is in the back lot. Do not park in guest spots. Street parking is available on Oak Street after 6pm."
            className="input w-full h-24 resize-none"
          />
        </div>

        {/* Training Materials & Menu */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-slate-900">Training Resources & Documents</h2>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-blue-800">
              <strong>Tip:</strong> Upload your documents to Google Drive, Dropbox, or your website, then paste the shareable link here.
              Make sure links are set to "Anyone with the link can view".
            </p>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                <Link2 className="w-4 h-4 inline mr-1" />
                Menu URL
              </label>
              <input
                type="url"
                value={formData.menuUrl || ''}
                onChange={(e) => handleChange('menuUrl', e.target.value)}
                placeholder="https://drive.google.com/... or https://yourrestaurant.com/menu"
                className="input w-full"
              />
              <p className="text-xs text-slate-500 mt-1">Link to your menu PDF or online menu page</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                <Link2 className="w-4 h-4 inline mr-1" />
                Training Materials URL
              </label>
              <input
                type="url"
                value={formData.trainingMaterialsUrl || ''}
                onChange={(e) => handleChange('trainingMaterialsUrl', e.target.value)}
                placeholder="https://docs.google.com/document/d/..."
                className="input w-full"
              />
              <p className="text-xs text-slate-500 mt-1">Link to training videos, guides, or documents</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                <Link2 className="w-4 h-4 inline mr-1" />
                Employee Handbook URL
              </label>
              <input
                type="url"
                value={formData.employeeHandbookUrl || ''}
                onChange={(e) => handleChange('employeeHandbookUrl', e.target.value)}
                placeholder="https://docs.google.com/document/d/..."
                className="input w-full"
              />
              <p className="text-xs text-slate-500 mt-1">Link to your employee handbook or policies document</p>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <User className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold text-slate-900">Contact Information</h2>
          </div>
          <p className="text-sm text-slate-500 mb-3">
            Who should new hires contact with questions?
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Contact Name</label>
              <input
                type="text"
                value={formData.contactName || ''}
                onChange={(e) => handleChange('contactName', e.target.value)}
                placeholder="John Smith"
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
              <input
                type="tel"
                value={formData.contactPhone || ''}
                onChange={(e) => handleChange('contactPhone', e.target.value)}
                placeholder="(555) 123-4567"
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.contactEmail || ''}
                onChange={(e) => handleChange('contactEmail', e.target.value)}
                placeholder="manager@restaurant.com"
                className="input w-full"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Floating Save Button for mobile */}
      {hasChanges && (
        <div className="fixed bottom-6 right-6 md:hidden">
          <button
            onClick={handleSave}
            disabled={saveMutation.isPending}
            className="btn btn-primary shadow-lg flex items-center gap-2"
          >
            {saveMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save
          </button>
        </div>
      )}
    </div>
  );
}
